import { Component } from '../../scripts/component.js';

/**
 * Syncs variant picker size selection to the release reminder drawer's
 * select[name="size"], and updates that select when the size unit
 * selector switches units (EU/US/UK).
 *
 * Place this component as a wrapper or sibling near the variant picker
 * inside the release product section.
 */
class ReleaseVariantSync extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Defer to next frame so all child components are mounted
    requestAnimationFrame(() => this.init());
  }

  init() {
    // Collect ALL size selects (block may render in both desktop and mobile)
    this.sizeSelects = [...this.querySelectorAll('select[name="size"]')];
    if (!this.sizeSelects.length) return;

    // Store original option values for unit conversion on every select
    for (const select of this.sizeSelects) {
      for (const option of select.options) {
        if (option.value && !option.disabled) {
          option.setAttribute('data-original-size', option.value);
        }
      }
    }

    this.setupVariantSync();
    this.setupUnitSync();
  }

  /**
   * 1. Listen for changes on the variant picker (radio buttons or selects)
   *    and pre-select the matching size in the drawer's select[name="size"].
   */
  setupVariantSync() {
    const section = this.closest('.release-product') || document;

    // Use event delegation on variant-picker elements
    const variantPickers = section.querySelectorAll('variant-picker');
    variantPickers.forEach((picker) => {
      picker.addEventListener('change', (e) => {
        const target = e.target;
        if (!target) return;

        let selectedValue = '';
        if (target instanceof HTMLSelectElement) {
          selectedValue = target.value;
        } else if (target instanceof HTMLInputElement && target.type === 'radio') {
          selectedValue = target.value;
        }

        if (selectedValue) {
          this.syncSizeToSelect(selectedValue);
        }
      });
    });
  }

  /**
   * Match the selected variant size to the drawer select option.
   * Tries matching by original-size data attribute, then by current value/text.
   */
  syncSizeToSelect(sizeValue) {
    if (!this.sizeSelects?.length) return;

    for (const select of this.sizeSelects) {
      for (const option of select.options) {
        if (option.disabled) continue;

        const originalSize = option.getAttribute('data-original-size') || option.value;
        const displayText = option.textContent.trim();

        if (originalSize === sizeValue || option.value === sizeValue || displayText === sizeValue) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    }
  }

  /**
   * 2. Listen for size unit selector changes (EU/US/UK)
   *    and update the drawer select option texts accordingly.
   */
  setupUnitSync() {
    // Observe the product-size-unit-selector for unit changes
    const unitSelector = document.querySelector('product-size-unit-selector');
    if (!unitSelector) return;

    // Use MutationObserver to detect when data-active changes on unit buttons
    const observer = new MutationObserver(() => {
      this.updateSelectOptionsFromUnitSelector(unitSelector);
    });

    observer.observe(unitSelector, {
      attributes: true,
      attributeFilter: ['data-active'],
      subtree: true,
    });

    // Also listen for clicks directly as a fallback
    unitSelector.addEventListener('click', () => {
      requestAnimationFrame(() => {
        this.updateSelectOptionsFromUnitSelector(unitSelector);
      });
    });

    // Run once on init to match the current active unit
    this.updateSelectOptionsFromUnitSelector(unitSelector);
  }

  /**
   * Reads the current unit from the unit selector,
   * looks up conversion data, and updates select option texts.
   */
  updateSelectOptionsFromUnitSelector(unitSelector) {
    if (!this.sizeSelects?.length || !unitSelector) return;

    // Get the size guide data from the component instance
    const sizeGuideData = unitSelector.productSizeGuideData;
    if (!sizeGuideData) return;

    // Find the active unit
    const activeButton = unitSelector.querySelector('button[data-active="true"]');
    if (!activeButton) return;

    const activeUnit = activeButton.textContent.trim();

    // Find the unit index from the input_ key (e.g., input_us: ["US", "EU", "UK"])
    const inputKey = Object.keys(sizeGuideData).find((k) => k.toLowerCase().startsWith('input_'));
    if (!inputKey) return;

    const unitsArray = sizeGuideData[inputKey]; // e.g., ["US", "EU", "UK"]
    const unitLabels = unitsArray.slice(1); // Skip first (it's the base reference)
    const unitIndex = unitLabels.indexOf(activeUnit);
    if (unitIndex === -1) return;

    const dataLookupIndex = unitIndex + 1; // +1 because first column is the base size

    // Update each option's text in ALL size selects
    for (const select of this.sizeSelects) {
      for (const option of select.options) {
        if (option.disabled || !option.getAttribute('data-original-size')) continue;

        const originalSize = option.getAttribute('data-original-size');
        const row = sizeGuideData[originalSize];

        if (row && row[dataLookupIndex] !== undefined) {
          const convertedSize = row[dataLookupIndex];
          option.textContent = convertedSize;
          option.value = convertedSize;
        } else {
          option.textContent = originalSize;
          option.value = originalSize;
        }
      }
    }
  }
}

if (!customElements.get('release-variant-sync')) {
  customElements.define('release-variant-sync', ReleaseVariantSync);
}
